import React, { useEffect, useState, useRef } from 'react';

import { validator, isValid, errors } from 'react-validator';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import ReactQuill from 'react-quill';

import {
  useDeleteAssignmentNoteMutation,
  useUpdateAssignmentMutation,
} from '@school_ify/schoolify-repository/src/shared/types';

import DropDown from 'components/DropDown/DropDown';
import NotesServices from 'domain/services/notes/NotesServices';
import { useAppSelector } from 'redux/hooks';
import { getUser } from 'redux/authentication/auth.slice';
import Errors from 'components/Errors/Errors';
import Card from 'components/Card/Card';
import { ReactComponent as MoreIcon } from 'assets/icons/more-vertical.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import IconTitleListItem from 'components/ListItem/IconTitleListItem';
import DeleteModal from 'components/DeleteModal/DeleteModal';
import './Notes.scoped.scss';
import pushRouter from 'hooks/useRouterHelper';
import useQueryParams from 'hooks/useQueryParams';

import ProfilePicture from '../../../../../clean/components/common/ProfilePicture/ProfilePicture';
import Loader from '../../../../../clean/components/Loader/Loader';

const Notes: React.FC = () => {
  const { t, i18n } = useTranslation();
  const noteQuillRef = useRef(null);
  const noteRef = useRef(null);
  const [deleteAssignmentNoteMutation] = useDeleteAssignmentNoteMutation();
  const [updateAssignmentNoteMutation] = useUpdateAssignmentMutation();
  const [loading, setLoading] = useState<boolean>(false);
  const { routePush } = pushRouter();
  const { lesson } = useQueryParams();
  const user = useAppSelector(getUser);
  const [notes, setNotes] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [validation, setValidation] = useState({});
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [selectedNoteId, setSelectedNoteId] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [noteToDelete, setNoteToDelete] = useState<string>('');

  function loadNotes() {
    validator(
      {
        lesson,
      },
      {
        lesson: ['required'],
      }
    );
    if (!isValid()) {
      return;
    }
    setLoading(true);
    new NotesServices()
      .get({
        _eq: lesson as string,
        _eq1: user.id,
      })
      .then(r => setNotes(r))
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    loadNotes();
  }, [window.location.search]);

  const handleSetSelectedNote = (id: string, content: string) => {
    // Strip all the html tags from content string
    const dom = new DOMParser().parseFromString(content, 'text/html');
    setSelectedNoteId(id);
    setNoteToDelete(dom.body.textContent);
  };
  const emptySelectedNote = () => {
    setSelectedNoteId('');
    setNote('');
  };

  const handleToggleDeleteModal = (id: string, content: string) => {
    handleSetSelectedNote(id, content);
    setDeleteModal(!deleteModal);
  };
  const handleDeleteAssignmentNote = () => {
    if (selectedNoteId) {
      deleteAssignmentNoteMutation({
        variables: {
          id: selectedNoteId,
        },
        onCompleted: () => {
          emptySelectedNote();
          setDeleteModal(false);
          loadNotes();
        },
      });
    }
  };

  async function insertNote() {
    validator(
      {
        note,
      },
      {
        note: ['required'],
      }
    );
    setValidation(errors());
    if (!isValid()) {
      return;
    }

    await new NotesServices().insert({
      assignment_id: lesson as string,
      user_id: user.id,
      content: note,
    });
    emptySelectedNote();
    loadNotes();
  }

  const enableEditMode = (id: string, content: string) => {
    setIsEditMode(true);
    setSelectedNoteId(id);
    setNote(content);
    noteQuillRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCloseNotes = () => {
    setSelectedNoteId('');
    setNote('');
    setIsEditMode(false);
    routePush({
      search: {
        section: '',
      },
    });
  };

  const updateNote = () => {
    validator(
      {
        selectedNoteId,
        note,
      },
      {
        selectedNoteId: ['required'],
        note: ['required'],
      }
    );
    setValidation(errors());
    if (!isValid()) {
      return;
    }
    updateAssignmentNoteMutation({
      variables: {
        id: selectedNoteId,
        content: note,
      },
      onCompleted: () => {
        emptySelectedNote();
        setIsEditMode(false);
        loadNotes();
        noteRef.current.scrollIntoView({ behavior: 'smooth' });
      },
    });
  };

  const upsertNote = async () => {
    if (isEditMode) updateNote();
    else await insertNote();
  };

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ align: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ],
  };

  return (
    <Card className="mt-1">
      <div ref={noteQuillRef} />
      <Card.Body>
        <DeleteModal
          openModal={deleteModal}
          handleModal={() => {
            setDeleteModal(!deleteModal);
          }}
          handleCloseModal={() => {
            setDeleteModal(!deleteModal);
          }}
          generalMessage={noteToDelete}
          handleDelete={handleDeleteAssignmentNote}
        />
        <ReactQuill
          style={{ border: 'none', borderRadius: '10px' }}
          theme="snow"
          value={note}
          modules={modules}
          onChange={val => {
            setNote(val);
          }}
        />
        <div className="quill-note-action-buttons-wrapper">
          <div className="flex mt-1 justify-end align-center gap-2">
            <label htmlFor="" className="cursor-pointer" onClick={handleCloseNotes}>
              {t('common:close')}
            </label>
            <label
              className="btn bg-primary text-white rounded-full nowrap  no-underline text-bold"
              onClick={upsertNote}
            >
              {isEditMode ? t('common:update') : t('common:save')}
            </label>
          </div>
          <div className="error-wrapper">
            <Errors prop="note" errors={validation} />
          </div>
        </div>

        <div className="mt-1">
          <h3>{t('study:Notes')}</h3>
          {loading && (
            <div className="flex justify-center">
              <Loader size={50} />
            </div>
          )}
          {notes.map(obj => {
            return (
              <div
                ref={selectedNoteId === obj.id ? noteRef : null}
                key={obj.id + obj.content}
                className="mt-1 border-radius-10px note"
                style={{ position: 'relative' }}
              >
                <div className="info flex">
                  <div className="profile-pic">
                    <ProfilePicture userId={user.id} />
                  </div>
                  <h1 className="name">{`${user.first_name} ${user.last_name}`}</h1>
                  <span>|</span>
                  <h2>{DateTime.fromISO(obj.created_at).setLocale(i18n.language).toRelative()}</h2>
                </div>
                <div className="content" dangerouslySetInnerHTML={{ __html: obj.content }}></div>
                <div
                  className="cursor-pointer p-1 border-radius-10px"
                  style={{ position: 'absolute', right: '5px', top: '5px' }}
                >
                  <DropDown
                    icon={<MoreIcon className="moreIcon" />}
                    boxStyle={{ width: '150px', padding: 0 }}
                  >
                    <IconTitleListItem
                      icon={<EditIcon />}
                      title={t('common:edit')}
                      handleOnClick={() => enableEditMode(obj.id, obj.content)}
                    />
                    <IconTitleListItem
                      icon={<DeleteIcon />}
                      title={t('common:delete')}
                      handleOnClick={() => handleToggleDeleteModal(obj.id, obj.content)}
                    />
                  </DropDown>
                </div>
                <div className="horizontal-line"></div>
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

export default Notes;
